import React, { useState } from 'react';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import TimelineIcon from '@mui/icons-material/Timeline';
import Typography from '@mui/material/Typography';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import { MasterMessage } from '../../../masters/masterMessages';
import Swal from 'sweetalert2';
import { useAuthContext } from '../../../contexts/authContext';
import { useDispatch, useSelector } from 'react-redux';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { RootState } from '../../../redux/store';
import { types } from '../../../redux/types';
import { asyncPatchCloseActivityTimes } from '../../../services/activityTimes.service';
import { AppBar, Drawer, DrawerHeader } from './components';
import Sidebar from './sidebar';
import { validatePermission } from '../../../services/auth.service';

interface Option {
    route?: string | undefined;
    label: string;
    icon: JSX.Element | string;
    subItems?: any[];
    permissions?: any[];
}

type Props = {
    modules: Option[];
};

function Navbar({ modules }: Props) {
    const dispatch: any = useDispatch();
    const { logout, agentIdLog, channel } = useAuthContext();
    const { logUser, channelList } = useSelector(
        (state: RootState) => state.general
    );
    const { name, surname, type } = logUser || {};
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { titleLogout, messageLogout } = MasterMessage.auth.logout;

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const statusEnd = async () => {
        asyncPatchCloseActivityTimes({
            agentId: agentIdLog,
            endTime: new Date(),
        }).finally(() => {
            dispatch({
                type: types.logoutLogin,
                payload: '',
            });
            logout();
        });
    };

    const logoutClick = () => {
        handleClose();
        Swal.fire({
            title: titleLogout,
            text: messageLogout,
            icon: 'error',
            iconColor: '#1d1d1d',
            showDenyButton: true,
            denyButtonText: 'NO',
            allowOutsideClick: false,
            denyButtonColor: '#1d1d1d',
            confirmButtonText: 'SI',
            confirmButtonColor: '#1d1d1d',
            customClass: 'swal-wide-class',
        }).then((response) => {
            if (response.isConfirmed) {
                statusEnd();
            }
        });
    };

    return (
        <>
            <AppBar position="fixed" open={open}>
                <Toolbar>
                    <IconButton
                        color="secondary"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            marginRight: 5,
                            ...(open && { display: 'none' }),
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <TimelineIcon
                        color="secondary"
                        sx={{
                            display: {
                                xs: open ? 'none' : 'flex',
                                sm: 'flex',
                                md: 'flex',
                            },
                            mr: 1,
                        }}
                    />
                    <Typography
                        color="secondary"
                        variant="h6"
                        component="div"
                        sx={{
                            flexGrow: 1,
                            mr: 2,
                            fontFamily: 'monospace',
                            fontSize: { lg: 25, md: 21, xs: 15 },
                            display: {
                                xs: open ? 'none' : 'flex',
                                sm: 'flex',
                                md: 'flex',
                            },
                            letterSpacing: '.3rem',
                            textDecoration: 'none',
                        }}
                    >
                        ON TIME
                    </Typography>
                    <div className={open ? 'navbar--div--perfil' : ''}>
                        <IconButton
                            edge="end"
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            color="secondary"
                        >
                            <Typography
                                color="secondary"
                                component="div"
                                sx={{
                                    flexGrow: 1,
                                    fontSize: { lg: 15, md: 12.5, xs: 10 },
                                    fontFamily: 'monospace',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    textTransform: 'uppercase',
                                    mr: 1,
                                }}
                            >
                                {name !== undefined
                                    ? name + ' ' + surname
                                    : 'Username'}
                            </Typography>
                            <AccountCircle color="secondary" />
                            <Typography
                                color="secondary"
                                component="div"
                                sx={{
                                    flexGrow: 1,
                                    fontSize: { lg: 15, md: 12.5, xs: 10 },
                                    display: {
                                        xs: open ? 'none' : 'flex',
                                        sm: 'flex',
                                        md: 'flex',
                                    },
                                    fontFamily: 'monospace',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    textTransform: 'uppercase',
                                    ml: 1,
                                }}
                            >
                                {type || 'Rol'}
                            </Typography>
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            <MenuItem onClick={logoutClick}>
                                Cerrar Sesión
                            </MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
            <ClickAwayListener
                mouseEvent="onMouseDown"
                touchEvent="onTouchStart"
                onClickAway={() => open && handleDrawerClose()}
            >
                <Drawer
                    variant="permanent"
                    open={open}
                    onClose={(_, reason) => reason === 'backdropClick'}
                >
                    <DrawerHeader>
                        <IconButton onClick={handleDrawerClose}>
                            <ChevronLeftIcon color="secondary" />
                        </IconButton>
                    </DrawerHeader>
                    <Divider />
                    <List>
                        {modules.map(
                            (
                                { route, label, icon, subItems, permissions },
                                i
                            ) => (
                                <div key={i}>
                                    {(!permissions ||
                                        validatePermission(
                                            permissions,
                                            channelList,
                                            type,
                                            channel
                                        )) && (
                                        <Sidebar
                                            route={route}
                                            label={label}
                                            icon={icon}
                                            subItems={subItems}
                                            open={open}
                                            type={type}
                                        />
                                    )}
                                </div>
                            )
                        )}
                    </List>
                </Drawer>
            </ClickAwayListener>
        </>
    );
}

export default Navbar;
